import { Component, computed, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, merge, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, startWith, switchMap } from 'rxjs/operators';
import { Employees } from 'src/app/afas-classes/employees.class';
import { Feature } from 'src/app/classes/auth/feature.class';
import { Functionality } from 'src/app/classes/auth/functionality';
import { Role } from 'src/app/classes/auth/role.class';
import { Entity } from 'src/app/classes/entity.class';
import { DefaultStand, TraveltimeWeekend, TraveltimeWorkday, User } from 'src/app/classes/user.class';
import { CodaltComponent } from 'src/app/codalt.component';
import { AfasService } from 'src/app/services/afas.service';
import { AuthorisationService } from 'src/app/services/auth/authorisation.service';
import { Features } from 'src/app/services/auth/features';
import { Functionalities } from 'src/app/services/auth/functionalities';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog-service/confirm-dialog.service';
import { EntitiesService } from 'src/app/services/entities/entities.service';
import { EntityTypeCode } from 'src/app/services/entities/entity-type.class';
import { FileStorage } from 'src/app/services/file.service';
import { LocationService } from 'src/app/services/location.service';
import {
    QueryLoggingActiveDuration,
    QueryLoggingDuration,
    QueryLoggingEntry,
    QueryLoggingSettings,
    USER_SETTINGS_MODULE_KEYS,
    UserSettings,
} from 'src/app/services/user/user-settings';
import { UserService } from 'src/app/services/user/user.service';
import { Settings } from 'src/app/settings.class';
import { CustomerProject } from '../../../classes/customer-project.class';
import { Customer } from '../../../classes/customer.class';
import { ApiResponse } from '../../../services/api/api.service';
import { CustomerProjectService } from '../../../services/customer-project.service';
import { CustomerService } from '../../../services/customer.service';
import { AddCustomerProjectModalComponent } from '../add-customer-project-modal/add-customer-project-modal.component';

@Component({
    selector: 'app-user-edit',
    templateUrl: './user-edit.component.html',
    styleUrls: ['./user-edit.component.scss'],
    standalone: false,
})
export class UserEditComponent extends CodaltComponent implements OnInit, OnDestroy {
    @Input() user: User;

    TraveltimeWorkday = TraveltimeWorkday;
    TraveltimeWeekend = TraveltimeWeekend;
    DefaultStand = DefaultStand;
    FileStorage = FileStorage;

    showHelp = false;

    saving = false;
    title = '';
    form: FormGroup;
    employees: Employees[];
    employee: Employees;
    hourCheckUsers: User[];
    rolesMap: Map<string, Role>;
    roles: Role[];
    uniqueRoles: Role[];
    private hiddenRoleKeys: string[] = [];
    readonly userFunctionalities = signal<Functionality[]>([]);
    readonly functionalityFilter = signal('');
    readonly filteredUserFunctionalities = computed(() => {
        const filter = this.functionalityFilter().trim().toLocaleLowerCase();

        return this.userFunctionalities().filter(
            functionality =>
                functionality.name.toLocaleLowerCase().includes(filter) ||
                functionality.key.toLocaleLowerCase().includes(filter)
        );
    });
    readonly userFeatures = signal<Feature[]>([]);
    readonly featureFilter = signal('');
    readonly filteredUserFeatures = computed(() => {
        const filter = this.featureFilter().trim().toLocaleLowerCase();

        return this.userFeatures().filter(
            feature =>
                feature.name.toLocaleLowerCase().includes(filter) || feature.key.toLocaleLowerCase().includes(filter)
        );
    });
    entityCars: Entity[];
    users: User[];
    customers: Customer[] = [];
    customerSearch$ = new Subject<string>();
    customersLoading = false;

    travelTimeNameMap = new Map<any, string>([
        [TraveltimeWorkday.Geen, 'Geen'],
        [TraveltimeWorkday.Staffel, 'Staffel'],
        [TraveltimeWorkday.Werkelijk, 'Werkelijk'],
    ]);

    constructor(
        private userService: UserService,
        private authorisationService: AuthorisationService,
        private entitiesService: EntitiesService,
        private route: ActivatedRoute,
        private router: Router,
        public afasService: AfasService,
        private confirmDialogService: ConfirmDialogService,
        protected locationService: LocationService,
        private customerService: CustomerService,
        private customerProjectService: CustomerProjectService,
        private matDialog: MatDialog
    ) {
        super();
    }

    ngOnInit() {
        // Load roles map before user loading
        this.subscriptions.add(
            this.authorisationService.getRolesMap().subscribe(rolesMap => {
                this.rolesMap = rolesMap;
                this.roles = this.sortRoles(
                    Array.from(rolesMap.values()).filter(
                        r =>
                            !r.is_unique &&
                            (!r.restricted || AuthorisationService.hasRole(this.environment.roles.Codalt))
                    )
                );
                // The unique role decides whether the user is internal or external
                const manageInternal = AuthorisationService.hasFunctionality(Functionalities.UserManagement);
                const manageExternal = AuthorisationService.hasFunctionality(Functionalities.UserManagementExternal);
                this.uniqueRoles = this.sortUniqueRoles(
                    Array.from(rolesMap.values()).filter(
                        r =>
                            r.is_unique &&
                            (!r.restricted || AuthorisationService.hasRole(this.environment.roles.Codalt)) &&
                            (r.functionalities?.some(f => f.key === Functionalities.External)
                                ? manageExternal
                                : manageInternal)
                    )
                );

                this.subscriptions.add(
                    this.route.params.subscribe((params: { userId: number }) => {
                        this.subscriptions.add(
                            this.userService.getSingle(+params.userId).subscribe(user => {
                                this.user = user;

                                this.setAfasEmployee();

                                if (
                                    AuthorisationService.hasAnyFunctionality([
                                        Functionalities.GeoRead,
                                        Functionalities.GeoRestricted,
                                        Functionalities.GeoManage,
                                    ]) &&
                                    !AuthorisationService.hasFunctionality(Functionalities.AccessAllCustomers, user)
                                ) {
                                    this.loadCustomers();
                                }
                                this.createForm(user);
                            })
                        );
                    })
                );
            })
        );

        // Only used by the internal user fields
        if (!AuthorisationService.hasFunctionality(Functionalities.UserManagement)) {
            return;
        }

        this.subscriptions.add(
            this.userService.getList(false, true, true, false).subscribe(users => {
                this.users = users;

                this.hourCheckUsers = users.filter(u =>
                    AuthorisationService.hasAnyFunctionality(
                        [Functionalities.HourCheckRestricted, Functionalities.HourCheck],
                        u
                    )
                );
            })
        );

        this.subscriptions.add(
            this.afasService.getEmployees().subscribe(emp => {
                this.employees = emp.data;
                this.setAfasEmployee();
            })
        );

        this.subscriptions.add(
            this.entitiesService.getByType(EntityTypeCode.Personenauto).subscribe(entities => {
                this.entityCars = entities.filter(
                    e => e.enddate === null || new Date(e.enddate).getTime() >= new Date().getTime()
                );
            })
        );
    }

    setAfasEmployee() {
        this.employee = this.employees?.find(e => e.employeeId === this.user?.afas_employee_id);
    }

    get userAddressMapUrl(): string {
        const address = [this.user.street, this.user.housenumber, this.user.zipcode, this.user.town]
            .filter(Boolean)
            .join(' ');
        return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    save() {
        const currentUser = this.user;
        if (!currentUser) return;

        const formValues = this.form.value;

        // Check if both hours_checked_by and hours_checked_by_role are set
        if (formValues.hours_checked_by && formValues.hours_checked_by_role) {
            this.confirmDialogService
                .confirm({
                    title: 'Urencontrole door dubbel ingesteld',
                    text: 'Het is niet toegestaan om zowel "Urencontrole door medewerker" als "Urencontrole door rol" in te stellen.',
                    confirm: 'Sluiten',
                })
                .then(() => {})
                .catch(() => {});
        } else {
            this.performSave(currentUser, formValues);
        }
    }

    private performSave(currentUser: User, formValues: any) {
        // Combine regular and unique roles into a single array
        const regularRoles = formValues.regularRoles || [];
        const uniqueRole = formValues.uniqueRole;
        const combinedRoles = [...regularRoles, ...this.hiddenRoleKeys, ...(uniqueRole ? [uniqueRole] : [])];

        const profilePicturePrefix = Settings.RESOURCES_ENDPOINT + FileStorage.ProfilePictures + '/';
        const profilePicture = formValues.profile_picture?.startsWith(profilePicturePrefix)
            ? formValues.profile_picture.substring(profilePicturePrefix.length)
            : formValues.profile_picture;

        // Querylogging fields are saved separately as a settings module, not as User fields
        const { queryLoggingSlowDuration, queryLoggingAllDuration, ...userFormValues } = formValues;

        Object.assign(currentUser, {
            ...userFormValues,
            profile_picture: profilePicture,
            roles: combinedRoles,
        });
        this.saving = true;

        Promise.all([this.userService.saveUser(currentUser), this.saveQueryLoggingSettings(currentUser)])
            .then(() => {
                this.form.markAsPristine();
                this.router.navigate(['/gebruikers']);
            })
            .catch(() => (this.saving = false));
    }

    saveQueryLogging() {
        const settings = this.buildQueryLoggingSettings(
            this.form.get('queryLoggingSlowDuration')?.value,
            this.form.get('queryLoggingAllDuration')?.value,
            this.user?.settings?.modules?.queryLogging
        );

        this.userService
            .saveUserSettingsForUser(this.user.id, USER_SETTINGS_MODULE_KEYS.QUERY_LOGGING, settings)
            .subscribe(response => this.applyQueryLoggingResponse(response.data));
    }

    stopAllQueryLogging() {
        const settings: QueryLoggingSettings = {
            slow: { duration: null, expires: null },
            all: { duration: null, expires: null },
        };

        this.userService
            .saveUserSettingsForUser(this.user.id, USER_SETTINGS_MODULE_KEYS.QUERY_LOGGING, settings)
            .subscribe(response => this.applyQueryLoggingResponse(response.data));
    }

    private applyQueryLoggingResponse(settings: UserSettings) {
        this.user.settings = settings;
        const queryLogging = settings.modules.queryLogging;

        const slowControl = this.form.get('queryLoggingSlowDuration');
        slowControl?.setValue(queryLogging.slow.duration ?? 'off');
        slowControl?.markAsPristine();

        const allControl = this.form.get('queryLoggingAllDuration');
        allControl?.setValue(queryLogging.all.duration ?? 'off');
        allControl?.markAsPristine();
    }

    private saveQueryLoggingSettings(user: User): Promise<void> {
        const slowControl = this.form.get('queryLoggingSlowDuration');
        const allControl = this.form.get('queryLoggingAllDuration');

        if (
            !AuthorisationService.hasFunctionality(Functionalities.UserTakeover) ||
            (!slowControl?.dirty && !allControl?.dirty)
        ) {
            return Promise.resolve();
        }

        const settings = this.buildQueryLoggingSettings(
            slowControl.value,
            allControl.value,
            this.user?.settings?.modules?.queryLogging
        );

        return new Promise((resolve, reject) => {
            this.userService
                .saveUserSettingsForUser(user.id, USER_SETTINGS_MODULE_KEYS.QUERY_LOGGING, settings)
                .subscribe({
                    next: response => {
                        this.applyQueryLoggingResponse(response.data);
                        resolve();
                    },
                    error: error => reject(error),
                });
        });
    }

    private buildQueryLoggingSettings(
        slowDuration: QueryLoggingDuration,
        allDuration: QueryLoggingDuration,
        existing?: QueryLoggingSettings
    ): QueryLoggingSettings {
        return {
            slow: this.buildQueryLoggingEntry(slowDuration, existing?.slow),
            all: this.buildQueryLoggingEntry(allDuration, existing?.all),
        };
    }

    private buildQueryLoggingEntry(duration: QueryLoggingDuration, existing?: QueryLoggingEntry): QueryLoggingEntry {
        if (duration === 'off') {
            return { duration: null, expires: null };
        }

        if (duration === existing?.duration && this.activeQueryLoggingExpiresAt(existing?.expires)) {
            return existing;
        }

        return {
            duration,
            expires: new Date(Date.now() + UserEditComponent.QUERY_LOGGING_DURATION_MS[duration]).toISOString(),
        };
    }

    private static readonly QUERY_LOGGING_DURATION_MS: Record<QueryLoggingActiveDuration, number> = {
        '1h': 60 * 60 * 1000,
        '1d': 24 * 60 * 60 * 1000,
        '1w': 7 * 24 * 60 * 60 * 1000,
    };

    get queryLoggingSlowExpiresAt(): Date | null {
        return this.activeQueryLoggingExpiresAt(this.user?.settings?.modules?.queryLogging?.slow?.expires);
    }

    get queryLoggingAllExpiresAt(): Date | null {
        return this.activeQueryLoggingExpiresAt(this.user?.settings?.modules?.queryLogging?.all?.expires);
    }

    private activeQueryLoggingExpiresAt(expires?: string | null): Date | null {
        const expiresAt = this.Utils.newDate(expires);
        return this.Utils.getTimeOrNull(expiresAt) > Date.now() ? expiresAt : null;
    }

    openChangesBackActionCheck(): Promise<boolean> {
        return new Promise(resolve => {
            if (this.form?.dirty) {
                this.confirmDialogService
                    .confirm({
                        title: 'Niet opgeslagen wijzigingen',
                        text: `Wilt u de niet opgeslagen wijzigingen verwerpen?`,
                        confirm: 'Hier blijven',
                        cancel: 'Wijzigingen verwerpen',
                    })
                    .then(
                        () => {
                            resolve(false);
                        },
                        () => {
                            resolve(true);
                        }
                    );
            } else {
                resolve(true);
            }
        });
    }

    isRegularRoleChecked(roleKey: string): boolean {
        const regularRoles = this.form?.get('regularRoles')?.value || [];
        return regularRoles.includes(roleKey);
    }

    onRegularRoleCheckboxChange(roleKey: string, checked: boolean) {
        const currentRoles = this.form?.get('regularRoles')?.value || [];
        const updatedRoles = checked ? [...currentRoles, roleKey] : currentRoles.filter(key => key !== roleKey);
        this.form?.get('regularRoles')?.setValue(updatedRoles);
        this.form?.get('regularRoles')?.markAsDirty();
    }

    copyFunctionality(functionality: Functionality) {
        const enumName = Object.entries(Functionalities).find(([, value]) => value === functionality.key)?.[0];

        if (enumName) {
            void navigator.clipboard.writeText(`Functionalities.${enumName}`);
        }
    }

    copyFeature(feature: Feature) {
        const enumName = Object.entries(Features).find(([, value]) => value === feature.key)?.[0];

        if (enumName) {
            void navigator.clipboard.writeText(`Features.${enumName}`);
        }
    }

    private createForm(user: User) {
        this.title = user.id ? 'aanpassen' : 'toevoegen';

        const userRoleKeys = user.roles ? user.roles.map(r => r.key) : [];
        const regularRoleKeys = userRoleKeys.filter(roleKey => this.roles.some(r => r.key === roleKey));
        const uniqueRoleKey = userRoleKeys.find(roleKey => this.uniqueRoles.some(r => r.key === roleKey)) ?? null;
        // Roles not shown in the form (e.g. restricted for non-Codalt) must be kept on save
        this.hiddenRoleKeys = userRoleKeys.filter(
            roleKey => !this.roles.some(r => r.key === roleKey) && !this.uniqueRoles.some(r => r.key === roleKey)
        );

        this.form = new FormGroup({
            name: new FormControl(user.name, [Validators.required, Validators.minLength(3)]),
            email: new FormControl(user.email, [Validators.required, Validators.email]),
            uniqueRole: new FormControl(uniqueRoleKey, [Validators.required]),
            regularRoles: new FormControl(regularRoleKeys),
            default_stand: new FormControl(user.default_stand),
            traveltime_workday: new FormControl(user.traveltime_workday),
            traveltime_weekend: new FormControl(user.traveltime_weekend),
            updated_at: new FormControl(user.updated_at),
            overtime: new FormControl(user.overtime),
            hours_checked_by: new FormControl(user.hours_checked_by),
            hours_checked_by_role: new FormControl(user.hours_checked_by_role),
            car_id: new FormControl(user.car_id),
            employees: new FormControl(user.employees?.map(u => u.id)),
            customers: new FormControl(user.customers?.map(c => c.id)),
            profile_picture: new FormControl(
                user.profile_picture
                    ? Settings.RESOURCES_ENDPOINT + FileStorage.ProfilePictures + '/' + user.profile_picture
                    : null
            ),
            queryLoggingSlowDuration: new FormControl<QueryLoggingDuration>(
                this.activeQueryLoggingExpiresAt(user.settings?.modules?.queryLogging?.slow?.expires)
                    ? (user.settings.modules.queryLogging.slow.duration ?? 'off')
                    : 'off'
            ),
            queryLoggingAllDuration: new FormControl<QueryLoggingDuration>(
                this.activeQueryLoggingExpiresAt(user.settings?.modules?.queryLogging?.all?.expires)
                    ? (user.settings.modules.queryLogging.all.duration ?? 'off')
                    : 'off'
            ),
        });
        if (
            user.roles.filter(
                r =>
                    this.rolesMap.get(r.key)?.restricted && !AuthorisationService.hasRole(this.environment.roles.Codalt)
            )?.length
        ) {
            this.form.disable();
        }

        if (AuthorisationService.hasFunctionality(Functionalities.CodaltMode)) {
            const form = this.form;
            const hiddenRoles = user.roles.filter(
                role => !this.roles.some(r => r.key === role.key) && !this.uniqueRoles.some(r => r.key === role.key)
            );
            this.subscriptions.add(
                forkJoin({
                    functionalities: this.authorisationService.getFunctionalities(),
                    features: this.authorisationService.getFeatures(),
                }).subscribe(({ functionalities, features }) => {
                    this.subscriptions.add(
                        merge(form.get('regularRoles').valueChanges, form.get('uniqueRole').valueChanges)
                            .pipe(startWith(null))
                            .subscribe(() => {
                                const roleKeys = [
                                    ...(form.get('regularRoles').value || []),
                                    form.get('uniqueRole').value,
                                ];
                                const previewUser: User = {
                                    ...user,
                                    roles: [
                                        ...hiddenRoles,
                                        ...Array.from(this.rolesMap.values()).filter(role =>
                                            roleKeys.includes(role.key)
                                        ),
                                    ],
                                };
                                this.userFunctionalities.set(
                                    functionalities.filter(functionality =>
                                        AuthorisationService.hasFunctionality(
                                            functionality.key as Functionalities,
                                            previewUser
                                        )
                                    )
                                );
                                this.userFeatures.set(
                                    features.filter(feature =>
                                        AuthorisationService.hasFeature(feature.key, previewUser)
                                    )
                                );
                            })
                    );
                })
            );
        }
    }

    private sortRoles(roles: Role[]): Role[] {
        return [...roles].sort((a, b) => {
            const categoryCompare = this.getRoleSortCategory(a) - this.getRoleSortCategory(b);

            if (categoryCompare !== 0) {
                return categoryCompare;
            }

            return this.compareRoleNames(a, b);
        });
    }

    private sortUniqueRoles(roles: Role[]): Role[] {
        return [...roles].sort((a, b) => {
            const externalCompare = Number(b.is_external) - Number(a.is_external);

            if (externalCompare !== 0) {
                return externalCompare;
            }

            const categoryCompare = this.getRoleSortCategory(a) - this.getRoleSortCategory(b);

            if (categoryCompare !== 0) {
                return categoryCompare;
            }

            return this.compareRoleNames(a, b);
        });
    }

    private getRoleSortCategory(role: Role): number {
        if (role.restricted) {
            return 0;
        }

        if (role.key === 'employed') {
            return 1;
        }

        return 2;
    }

    private compareRoleNames(a: Role, b: Role): number {
        return a.name.localeCompare(b.name, 'nl', {
            sensitivity: 'base',
        });
    }

    isUniqueRoleDisabled(role: Role): boolean {
        if (role.restricted && !AuthorisationService.hasRole(this.environment.roles.Codalt)) {
            return true;
        }

        const selectedRole = this.uniqueRoles.find(r => r.key === this.form.get('uniqueRole')?.value);

        return !!selectedRole?.is_external && !role.is_external;
    }

    isRoleDisabled(role: Role): boolean {
        return role.restricted && !AuthorisationService.hasRole(this.environment.roles.Codalt);
    }

    hasPlannableFunctionality(role: Role): boolean {
        return role.functionalities?.some(f => f.key === Functionalities.Plannable) ?? false;
    }

    /**
     * Of deze rol met de hand verloond wordt via de periodeverrekening. Medewerkers zonder deze
     * functionaliteit komen daar niet in voor; hun verlofregels gaan automatisch naar Afas.
     */
    hasSettleFunctionality(role: Role): boolean {
        return role.functionalities?.some(f => f.key === Functionalities.Settle) ?? false;
    }

    get hasPlannableRole(): boolean {
        return AuthorisationService.hasFunctionality(Functionalities.Plannable, this.user);
    }

    get isExternalUser(): boolean {
        return this.user?.roles?.some(r => r.is_external) ?? false;
    }

    get hasGeoFunctionality(): boolean {
        return AuthorisationService.hasAnyFunctionality(
            [Functionalities.GeoRead, Functionalities.GeoRestricted, Functionalities.GeoManage],
            this.user
        );
    }

    loadCustomers() {
        this.subscriptions.add(
            this.customerSearch$
                .pipe(
                    debounceTime(250),
                    distinctUntilChanged(),
                    switchMap(search => {
                        this.customersLoading = true;
                        return this.customerService.search(search).pipe(catchError(() => of([])));
                    })
                )
                .subscribe(customers => {
                    this.customers = this.mergeAssignedCustomers(customers);
                    this.customersLoading = false;
                })
        );

        // Trigger an initial empty search to populate items with user's assigned customers
        this.subscriptions.add(
            this.customerService.getList().subscribe(customers => {
                this.customers = this.mergeAssignedCustomers(customers);
            })
        );
    }

    // Ensure customers already assigned to the user always resolve a label in the ng-select,
    // even if they're filtered out or beyond the page limit of the general customers list/search.
    private mergeAssignedCustomers(customers: Customer[]): Customer[] {
        const assigned = this.user?.customers?.filter(c => !customers.some(existing => existing.id === c.id)) ?? [];

        return [...customers, ...assigned];
    }

    addTownProject() {
        this.subscriptions.add(
            this.matDialog
                .open(AddCustomerProjectModalComponent, {
                    panelClass: 'add-townproject-dialog',
                    maxWidth: '100vw',
                    maxHeight: '100vh',
                    data: {
                        user: this.user,
                    },
                })
                .afterClosed()
                .subscribe((user?: User) => {
                    if (!user) return;
                    this.user.customer_projects = user.customer_projects;
                    this.user.customer_projects_history = user.customer_projects_history;
                })
        );
    }

    removeTownProject(customerProject: CustomerProject) {
        this.subscriptions.add(
            this.customerProjectService
                .unfollow(customerProject.id, this.user.id)
                .subscribe((result: ApiResponse<User>) => {
                    if (result.success) {
                        this.user.customer_projects = result.data.customer_projects;
                        this.user.customer_projects_history = result.data.customer_projects_history;
                    } else {
                        this.confirmDialogService.confirm({
                            title: 'Uitschrijven mislukt',
                            text: 'Het is niet gelukt om je uit te schrijven voor ' + customerProject.name,
                        });
                    }
                })
        );
    }
}
